import React, { useState } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, Link, Alert, IconButton } from '@mui/material';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlined from '@mui/icons-material/VisibilityOffOutlined';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AuthLayout, { authInputSx, authSubmitSx } from './AuthLayout';

const API_URL = `${(process.env.REACT_APP_API_BASE || 'http://localhost:5000')}/api/auth`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) return setError('Password must be at least 8 characters.');
    if (password !== confirm) return setError('Passwords do not match.');
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/reset-password`, { token, password });
      setDone(true);
    } catch (err) {
      console.error('Reset password error', err);
      setError(err.response?.data?.message || 'Could not reach the server. Try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  // Arriving without a token means the link was mangled or typed by hand.
  if (!token) {
    return (
      <AuthLayout title="Reset your password">
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          This reset link is missing its token. Open the link from your email again, or request a new one.
        </Alert>
        <Button component={RouterLink} to="/forgot-password" fullWidth variant="contained" size="large" sx={authSubmitSx}>
          Request a new link
        </Button>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout title="Password updated" subtitle="You've been signed out everywhere for safety">
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          Your password has been changed. Sign in with your new password to continue.
        </Alert>
        <Button component={RouterLink} to="/login" fullWidth variant="contained" size="large" sx={authSubmitSx}>
          Sign in
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Choose a new password" subtitle="At least 8 characters" busy={submitting}>
      {error && <Alert severity="error" role="alert" sx={{ borderRadius: 2 }}>{error}</Alert>}
      <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }} component="label" htmlFor="reset-password">New password</Typography>
            <TextField
              id="reset-password"
              fullWidth
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              size="small"
              autoComplete="new-password"
              autoFocus
              disabled={submitting}
              InputProps={{
                endAdornment: (
                  <IconButton size="small" onClick={() => setShowPassword(p => !p)} aria-label={showPassword ? 'Hide password' : 'Show password'} edge="end" sx={{ p: 0.5 }}>
                    {showPassword ? <VisibilityOffOutlined sx={{ fontSize: 18 }} /> : <VisibilityOutlined sx={{ fontSize: 18 }} />}
                  </IconButton>
                ),
                sx: authInputSx,
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }} component="label" htmlFor="reset-confirm">Confirm new password</Typography>
            <TextField
              id="reset-confirm"
              fullWidth
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              size="small"
              autoComplete="new-password"
              disabled={submitting}
              InputProps={{ sx: authInputSx }}
            />
          </Box>
        </Box>
        <Button type="submit" fullWidth variant="contained" size="large" disabled={submitting} sx={authSubmitSx}>
          {submitting ? <CircularProgress size={22} color="inherit" /> : 'Set new password'}
        </Button>
      </form>
      <Typography variant="body2" sx={{ textAlign: 'center', fontSize: '0.8125rem', mt: 1 }}>
        <Link component={RouterLink} to="/login" underline="hover" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>Back to sign in</Link>
      </Typography>
    </AuthLayout>
  );
};

export default ResetPassword;
